// apps/web/src/features/clients/components/ClientContactRow.tsx
//
// One contact of a client. Calling and copying the number are the two things
// this row exists for during a working day, so both are direct controls rather
// than menu entries; the rarer lifecycle actions stay in the overflow menu.
//
// INVARIANT (AC-10): every destructive or state-changing confirmation names the
// contact it will affect — "Archive contact «Ivan Petrov»" — because the same
// button repeats on every row.
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientContact } from '@magimp/shared';
import {
  useArchiveClientContact,
  useRestoreClientContact,
  useSetClientContactPrimary,
} from '@magimp/shared-hooks';
import {
  Chip,
  ConfirmModal,
  ContactActions,
  IconButton,
  Menu,
  MenuItem,
  StatusBadge,
} from '../../../ui/index.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { useActionSize, useCoarsePointer, useTouchActionStyle } from '../useMobileFieldStyle.js';
import { CopyTextButton } from './CopyTextButton.js';
import { displayPhone, TelLink } from './clientRowParts.js';

export interface ClientContactRowProps {
  clientId: string;
  contact: ClientContact;
  /** True when archiving this contact still leaves the client with a contact. */
  hasOtherActive: boolean;
  /** False for read-only actors and for an archived parent client. */
  canManage: boolean;
  /** True while an ANCESTOR lifecycle write (the client's own) is in flight. */
  busy?: boolean;
  onEdit: (contact: ClientContact) => void;
}

type Pending = null | 'archive' | 'primary';

/** Structural shape of the no-argument contact lifecycle mutations. */
type VoidMutation = {
  mutate: (
    variables: undefined,
    options: { onSuccess: () => void; onError: (error: unknown) => void },
  ) => void;
};

export function ClientContactRow({
  clientId,
  contact,
  hasOtherActive,
  canManage,
  busy = false,
  onEdit,
}: ClientContactRowProps) {
  const { t } = useTranslation(['clients', 'common']);
  const show = useToastStore((s) => s.show);
  const touch = useCoarsePointer();
  const actionSize = useActionSize();
  const archive = useArchiveClientContact(clientId, contact.id);
  const restore = useRestoreClientContact(clientId, contact.id);
  const setPrimary = useSetClientContactPrimary(clientId, contact.id);
  const [confirm, setConfirm] = useState<Pending>(null);

  const isArchived = !!contact.archivedAt;
  const callLabel = t('clients:actions.callContact', { name: contact.fullName });
  // A lifecycle write in flight decides what this row may do next: offering
  // "restore" again (or "make primary" on top of an archive) sends a second
  // request whose verdict contradicts the first. The client's OWN archive counts
  // too — it is about to make every contact write illegal.
  const ownPending = archive.isPending || restore.isPending || setPrimary.isPending;
  const blocked = ownPending || busy;
  const blockedReason = ownPending
    ? t('clients:blocked.pendingAction')
    : t('clients:blocked.pendingAncestor');
  // Why archive is refused HERE, in the menu where the refusal is visible.
  const archiveHint = !hasOtherActive
    ? t('clients:blocked.archiveLastContact')
    : contact.isPrimary
      ? t('clients:blocked.archivePrimaryContact')
      : null;

  /** Same success/failure handling for all three lifecycle mutations. */
  const run = (mutation: VoidMutation, successText: string) => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        show({ tone: 'success', text: successText });
        setConfirm(null);
      },
      onError: (err: unknown) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  return (
    <li
      data-testid="client-contact"
      data-contact-id={contact.id}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-3)',
        flexWrap: 'wrap',
        padding: 'var(--s-3) var(--s-4)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        background: isArchived ? 'var(--bg-2)' : 'var(--bg-1)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{contact.fullName}</span>
          {contact.isPrimary && !isArchived && (
            <Chip tone="accent" icon="star">
              {t('clients:badges.primary')}
            </Chip>
          )}
          {isArchived && <StatusBadge status="archived" size="sm" />}
        </div>
        <div
          style={{
            fontSize: 'var(--fz-12)',
            color: 'var(--fg-3)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {displayPhone(contact.phone)}
        </div>
        {contact.comment && (
          <div style={{ fontSize: 'var(--fz-12)', color: 'var(--fg-3)', overflowWrap: 'anywhere' }}>
            {contact.comment}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
        {touch ? (
          <TelLink href={`tel:${contact.phone}`} label={callLabel} />
        ) : (
          <ContactActions
            actions={[{ icon: 'phone', href: `tel:${contact.phone}`, label: callLabel }]}
          />
        )}
        <CopyTextButton
          text={contact.phone}
          label={t('clients:actions.copyPhoneOf', { name: contact.fullName })}
          testId="client-copy-phone"
        />
        {canManage && (
          <Menu
            trigger={(triggerProps) => (
              <IconButton
                {...triggerProps}
                icon="more-vertical"
                size={actionSize}
                label={t('common:actions.more')}
              />
            )}
          >
            {/* Says why everything below is inert, instead of leaving the
                operator clicking a dead menu. */}
            {blocked && <MenuHint>{blockedReason}</MenuHint>}
            {!isArchived && (
              <>
                <TouchMenuItem disabled={blocked} onClick={() => onEdit(contact)}>
                  {t('common:actions.edit')}
                </TouchMenuItem>
                <TouchMenuItem
                  disabled={blocked || contact.isPrimary}
                  onClick={() => setConfirm('primary')}
                >
                  {t('clients:actions.setPrimary')}
                </TouchMenuItem>
                {contact.isPrimary && <MenuHint>{t('clients:blocked.alreadyPrimary')}</MenuHint>}
                <TouchMenuItem
                  disabled={blocked || contact.isPrimary || !hasOtherActive}
                  onClick={() => setConfirm('archive')}
                >
                  {t('common:actions.archive')}
                </TouchMenuItem>
                {/* The refusal is a rule with a way out; state the way out. */}
                {archiveHint !== null && <MenuHint>{archiveHint}</MenuHint>}
              </>
            )}
            {isArchived && (
              <TouchMenuItem
                disabled={blocked}
                onClick={() => run(restore, t('clients:toasts.contactRestored'))}
              >
                {t('common:actions.restore')}
              </TouchMenuItem>
            )}
          </Menu>
        )}
      </div>

      {confirm === 'archive' && (
        <ConfirmModal
          danger
          narrowFullWidth
          title={t('clients:confirm.archiveContactTitle')}
          text={t('clients:confirm.archiveContactMessage', { name: contact.fullName })}
          confirmLabel={t('common:actions.archive')}
          onConfirm={() => run(archive, t('clients:toasts.contactArchived'))}
          onClose={() => setConfirm(null)}
        />
      )}
      {confirm === 'primary' && (
        <ConfirmModal
          narrowFullWidth
          title={t('clients:confirm.primaryContactTitle')}
          text={t('clients:confirm.primaryContactMessage', { name: contact.fullName })}
          confirmLabel={t('clients:actions.setPrimary')}
          onConfirm={() => run(setPrimary, t('clients:toasts.contactPrimary'))}
          onClose={() => setConfirm(null)}
        />
      )}
    </li>
  );
}

/**
 * A menu entry that reaches the touch minimum without touching the shared
 * `Menu`: the height comes from the entry's OWN content, so raising the content
 * box raises the row. Desktop keeps the compact menu density.
 */
function TouchMenuItem({
  disabled,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  const actionStyle = useTouchActionStyle();
  return (
    <MenuItem disabled={disabled} onClick={onClick}>
      <span style={{ display: 'flex', alignItems: 'center', ...actionStyle }}>{children}</span>
    </MenuItem>
  );
}

/**
 * Why an entry above is unavailable, said inside the menu where the refusal is.
 *
 * WHY a disabled `MenuItem` and not a bare paragraph: a `role="menu"` panel does
 * not expose arbitrary text to assistive tech, so the explanation would be
 * visible to sighted users only. A disabled entry is announced as unavailable
 * and skipped by the menu's own arrow navigation.
 */
function MenuHint({ children }: { children: ReactNode }) {
  return (
    <MenuItem disabled onClick={() => undefined}>
      <span
        style={{
          display: 'block',
          maxWidth: 240,
          whiteSpace: 'normal',
          fontSize: 'var(--fz-11)',
        }}
      >
        {children}
      </span>
    </MenuItem>
  );
}
